/**
 * The operator's reason for a View-As session, asked for rather than assumed.
 *
 * WHY THIS EXISTS. The backend refuses an IMPERSONATE support action without a
 * reason of at least MIN_REASON_LENGTH characters, and records it on both sides
 * of the repo boundary. Every Admin entry point satisfied that gate with a
 * string literal — "Open Workspace from Tenant Management", "Impersonate User
 * from Platform Users" — so the audit trail recorded WHERE the button was, and
 * never WHY it was pressed. A reason gate nobody is asked to pass is not a
 * gate.
 *
 * ONE DIALOG, FOUR ENTRY POINTS. Tenants list, Tenant Detail users tab, Tenant
 * Overview, Platform Users, and now User Detail all open this with a `target`
 * and nothing else; the mint and redirect stay in `startViewAs`, which is the
 * single authority for the URL form. This file never builds a CRM URL.
 *
 * THE TAB IS OPENED IN THE CONFIRM CLICK. `startViewAs` documents why: a
 * `window.open` after an awaited mint is a blocked popup. The confirm button is
 * the operator's gesture, so the blank tab is opened synchronously in its
 * handler and handed in. No extra windows beyond that one — which is the same
 * one the entry points already opened before this dialog existed.
 *
 * Built on the repo's Dialog primitive (the same one Reset Password uses on
 * Tenant Detail), not a new modal library.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { startViewAs, VIEW_AS } from './startViewAs';

/**
 * Mirrors `shared.audit.reason.MIN_REASON_LENGTH` on the backend. A
 * structural test on that side asserts the two agree, so a shorter reason is
 * refused here with a sentence rather than there with a 422.
 */
export const MIN_REASON_LENGTH = 4;

/** Whitespace-only is no reason at all, which is how the backend reads it too. */
export const cleanReason = (reason) => (typeof reason === 'string' ? reason.trim() : '');

export const reasonIsAcceptable = (reason) => cleanReason(reason).length >= MIN_REASON_LENGTH;

// NO DEFAULT DESTINATION LIVES HERE ANY MORE.
//
// This was `process.env.REACT_APP_CRM_URL || 'http://localhost:3000'` — a
// build-time constant, one value for four environments, with a localhost
// fallback. `startViewAs` now uses the `crm_base_url` the server resolves from
// the tenant's own environment, which is the same authority that decides which
// runtime the grant is written to. The registry's rule is that components
// never carry a hostname; they ask it. This component now obeys that.

/** Who the session will be, in words the operator can check before confirming. */
export const describeTarget = (target) => {
  if (!target) return { who: '', where: '' };
  const who = target.userEmail
    ? (target.userName ? `${target.userName} (${target.userEmail})` : target.userEmail)
    : "the tenant's primary administrator";
  const where = target.tenantName || target.tenantId || '';
  return { who, where };
};

/**
 * @param {object}   props
 * @param {object|null} props.target   `{ tenantId, tenantName?, userId?, userEmail?, userName? }`;
 *                                     null closes the dialog
 * @param {Function} props.post        (path, body) => Promise<{data}> against the control-plane API
 * @param {Function} props.onClose     called when the dialog should close (cancel, or after success)
 * @param {object}   [props.notify]    `{ success, error }`; defaults to react-hot-toast
 */
export default function ImpersonationReasonDialog({
  target, post, onClose, notify = toast,
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  // A reason belongs to one target. Re-opening for someone else must not carry
  // the last operator's sentence across.
  useEffect(() => {
    setReason('');
    setTouched(false);
    setBusy(false);
  }, [target]);

  const open = !!target;
  const acceptable = reasonIsAcceptable(reason);
  const { who, where } = describeTarget(target);

  const confirm = async () => {
    setTouched(true);
    if (busy || !target) return;
    // Inside the click's own user-activation window — see startViewAs.
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    const res = await startViewAs({
      tab,
      post,
      tenantId: target.tenantId,
      userId: target.userId,
      reason: cleanReason(reason),
      // No destination is passed. The server resolves it from the tenant's
      // environment; see startViewAs.
    });
    setBusy(false);
    if (res.outcome === VIEW_AS.OPENED) {
      notify.success(`Opening workspace as ${res.email || who}`);
      onClose?.();
    } else {
      notify.error(res.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose?.(); }}>
      <DialogContent data-testid="impersonation-reason-dialog">
        <DialogHeader>
          <DialogTitle>Login as user</DialogTitle>
          <DialogDescription>
            You will be signed in as{' '}
            <span className="font-medium text-foreground" data-testid="impersonation-target-user">{who}</span>
            {where ? (
              <>
                {' '}in{' '}
                <span className="font-medium text-foreground" data-testid="impersonation-target-tenant">{where}</span>
              </>
            ) : null}
            , in a new tab. The session is audited on both sides whether or not you give a
            reason here, never needs their password, and a banner in their workspace lets you
            exit at any time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="impersonation-reason">Reason (optional)</Label>
          <Textarea
            id="impersonation-reason"
            rows={3}
            placeholder="Why you need this session — a ticket number, the customer's report, what you are checking"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={busy}
            data-testid="impersonation-reason"
          />
          {touched && cleanReason(reason).length > 0 && !acceptable && (
            <p className="text-xs text-muted-foreground" data-testid="impersonation-reason-error">
              Recorded as written. Under {MIN_REASON_LENGTH} characters is unlikely to mean
              anything to whoever reads the audit trail later — but it will not stop you.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            disabled={busy}
            data-testid="impersonation-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={confirm}
            disabled={busy}
            data-testid="impersonation-confirm"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
            ) : (
              <LogIn className="h-4 w-4 mr-2" aria-hidden="true" />
            )}
            Login as user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
